import { ReactElement, memo, useMemo } from "react";
import { ComicContentCardProps } from "../../types";
import { ComicGridCard } from "./ComicGridCard";
import { ComicHorizontalCard } from "./ComicHorizontalCard";
import { ComicListCard } from "./ComicListCard";
import { Link } from "@chakra-ui/react";
import { useSlideInMenu } from "../slideInMenu/hooks/useSlideInMenu";
import { useNavigateToReaderWithContext } from "../../screens/reader/hooks/useNavigateToReaderWithContext";
import { trackRecsysCardItemClick } from "../../analytics/recsys/tracking";
import { applyNavigationAnalyticsAttribution } from "../../analytics/params/navigationContextAttribution";

export const ComicCard = memo((
  props: ComicContentCardProps & {
    dontOpenComicDetails?: boolean;
  },
) => {
  const { openComicInfoMenu } = useSlideInMenu();
  const mode = props.layout.style.mode;

  const cards: { [key: string]: ReactElement } = useMemo(
    () => ({
      grid: <ComicGridCard {...props} />,
      horizontal: <ComicHorizontalCard {...props} />,
      list: <ComicListCard {...props} />,
      calendar: <ComicListCard {...props} />,
    }),
    [props],
  );

  const { goToCTAUrl, goToCTAUrlWithContext, navigationContext } =
    useNavigateToReaderWithContext({
    comicToNavigate: props.content,
    cardPosition: props.index,
    sectionPosition: props.sectionIndex,
    section: props.section,
    area: props.area,
  });

  if (cards[mode]) {
    return (
      <Link
        id={`comic-${props.content.id}`}
        href={props.content.url}
        width={props.fullWidth ? "100%" : "auto"}
        draggable={false}
        onClick={(e) => {
          e.preventDefault();
          const impressionId =
            props.section && props.sectionIndex !== undefined
              ? trackRecsysCardItemClick({
                  section: props.section,
                  sectionIndex: props.sectionIndex,
                  item: props.content,
                  itemIndex: props.trackingIndex ?? props.index ?? 0,
                })
              : undefined;
          const trackedNavigationContext = applyNavigationAnalyticsAttribution(
            navigationContext,
            { impressionId },
          );
          if (
            props.section?.source_type === "continue-reading" ||
            props.dontOpenComicDetails ||
            props.dontOpenInfo
          ) {
            if (impressionId) {
              goToCTAUrlWithContext({ impressionId });
            } else {
              goToCTAUrl();
            }
          } else {
            openComicInfoMenu(props?.content.slug, trackedNavigationContext);
          }
        }}
      >
        {cards[mode]}
      </Link>
    );
  }

  console.warn("Unable to determine layout mode for ComicCard");
  return null;
}, (prevProps, nextProps) => {
  // Only re-render if primitive values or IDs change
  // Avoid object comparisons (layout, section) as they would always fail
  return (
    prevProps.content.id === nextProps.content.id &&
    prevProps.content.is_favorite === nextProps.content.is_favorite &&
    prevProps.content.user_read_status?.uuid === nextProps.content.user_read_status?.uuid &&
    prevProps.content.user_read_status?.read_progress_percentage === nextProps.content.user_read_status?.read_progress_percentage &&
    prevProps.layout.style.mode === nextProps.layout.style.mode &&
    prevProps.layout.style.size === nextProps.layout.style.size &&
    prevProps.layout.display === nextProps.layout.display &&
    prevProps.section?.source_type === nextProps.section?.source_type &&
    prevProps.fullWidth === nextProps.fullWidth &&
    prevProps.dontOpenInfo === nextProps.dontOpenInfo &&
    prevProps.dontOpenComicDetails === nextProps.dontOpenComicDetails &&
    prevProps.index === nextProps.index &&
    prevProps.sectionIndex === nextProps.sectionIndex &&
    prevProps.area === nextProps.area
  );
});

ComicCard.displayName = 'ComicCard';
